/**
 * AI batch image edit — one AI image edit (Revamp / Visualise / Background) run
 * over every product of a listing collection that belongs to one business.
 *
 * Backed by `/api/v1/imagegen/batches` (product service). Results are applied to
 * the products automatically: the new image is put first and nothing is
 * removed. A finished batch can be reverted by its submitter or an admin.
 * Backend design: NineBackend/product/docs/ai-image-batch-design.md.
 *
 * Curators may only run batches for businesses they've claimed; admins may run
 * them for any business and are exempt from the per-business limit.
 */

import {
  ImageRevampFormat,
  ImageRevampFunctionality,
} from "./ImageRevampObject";

/** Same three edits as the single-photo revamp — one per batch, never mixed. */
export type AiBatchFunctionality = ImageRevampFunctionality;

export const AI_BATCH_FUNCTIONALITIES: readonly AiBatchFunctionality[] = [
  "ENHANCE",
  "VISUALISE",
  "CHANGE_BACKGROUND",
];

export type AiBatchStatus =
  | "PREPARING"
  | "SUBMITTED"
  | "INGESTING"
  | "COMPLETED"
  | "PARTIAL"
  | "FAILED"
  | "EXPIRED"
  | "CANCELLED";

export const AI_BATCH_STATUSES: readonly AiBatchStatus[] = [
  "PREPARING",
  "SUBMITTED",
  "INGESTING",
  "COMPLETED",
  "PARTIAL",
  "FAILED",
  "EXPIRED",
  "CANCELLED",
];

export type AiBatchItemStatus =
  | "QUEUED"
  | "SUBMITTED"
  | "APPLIED"
  | "FAILED"
  | "REVERTED";

export const AI_BATCH_ITEM_STATUSES: readonly AiBatchItemStatus[] = [
  "QUEUED",
  "SUBMITTED",
  "APPLIED",
  "FAILED",
  "REVERTED",
];

/**
 * The `status` query param on the list endpoint. Each value is a GROUP of batch
 * statuses, resolved server-side — see AI_BATCH_FILTER_STATUSES for the mapping
 * the client mirrors to keep its own list coherent after an action.
 */
export type AiBatchFilter = "ALL" | "IN_PROGRESS" | "DONE" | "FAILED";

/** Chips shown in the Tasks list. "ALL" stays a valid API filter but isn't
 *  offered — In progress is the default view; Done / Failed are opt-in. */
export const AI_BATCH_FILTERS: readonly AiBatchFilter[] = [
  "IN_PROGRESS",
  "DONE",
  "FAILED",
];

/** Mirrors `AiImageBatchStatus.forFilter` on the backend. `ALL` is every status. */
export const AI_BATCH_FILTER_STATUSES: Record<
  Exclude<AiBatchFilter, "ALL">,
  readonly AiBatchStatus[]
> = {
  IN_PROGRESS: ["PREPARING", "SUBMITTED", "INGESTING"],
  DONE: ["COMPLETED", "PARTIAL"],
  FAILED: ["FAILED", "EXPIRED", "CANCELLED"],
};

/** A batch- or item-level failure. `code` is the backend's numeric string
 *  (e.g. "16050"); `message` is the server's safe detail, when it sent one. */
export interface AiBatchError {
  code: string;
  message: string | null;
}

/** One product's image within a batch. Only the detail endpoint returns these. */
export interface AiBatchItem {
  itemId: string;
  productId: string;
  /** Snapshot at submit time. Null until the backend ships the snapshot column. */
  productTitle: string | null;
  status: AiBatchItemStatus;
  /** Public URL of the product photo that was edited. */
  sourceUrl: string | null;
  /** Public URL of the AI result — present once APPLIED, and kept after REVERTED. */
  resultUrl: string | null;
  error: AiBatchError | null;
  completedAt: string | null;
}

/** A collection member left out at submit time, and why. */
export interface AiBatchSkippedProduct {
  productId: string;
  /** `PRODUCT_NOT_FOUND | NOT_IN_BUSINESS | NO_ELIGIBLE_IMAGE | ALREADY_IN_BATCH`,
   *  kept as a string so a reason added server-side still renders (with the
   *  server's own message) instead of being dropped. */
  reason: string;
  message: string | null;
}

export interface AiBatch {
  batchId: string;
  businessId: string;
  /** Snapshot at submit time. Null until the backend ships the snapshot column. */
  businessName: string | null;
  collectionId: string;
  /** Snapshot at submit time — the collection may since have been renamed. */
  collectionName: string | null;
  submittedBy: string;
  functionality: AiBatchFunctionality;
  instructions: string | null;
  status: AiBatchStatus;
  totalItems: number;
  succeededItems: number;
  failedItems: number;
  /** False for admin-submitted batches, which don't consume the business limit. */
  countsTowardLimit: boolean;
  /** Batch-level failure, when `status` is FAILED. */
  error: AiBatchError | null;
  createdAt: string | null;
  submittedAt: string | null;
  completedAt: string | null;
  revertedAt: string | null;
  /** Detail / cancel / revert responses only; null in the list view. */
  items: AiBatchItem[] | null;
  /** Submit response only; null everywhere else. */
  skipped: AiBatchSkippedProduct[] | null;
}

/** How much of a business's lifetime batch-edit limit is left for the caller. */
export interface AiBatchQuota {
  businessId: string;
  /** False when the feature is switched off — the submit flow is hidden. */
  enabled: boolean;
  /** True for admins: their batches neither hit nor consume the limit. */
  exempt: boolean;
  limit: number;
  used: number;
  remaining: number;
  maxItemsPerBatch: number;
}

/** Submit form — also the wire body for POST /batches (same fields; the
 *  uiToBackend converter only trims and drops empty optionals). `format` is
 *  omitted in v1 — each functionality's default shape is used (Revamp keeps the
 *  photo's shape, Visualise landscape, Background square). */
export interface SubmitAiBatchForm {
  businessId: string;
  collectionId: string;
  functionality: AiBatchFunctionality;
  instructions?: string;
  format?: ImageRevampFormat;
  /** Turbo: deeper, business-aware intelligence. Sent only when on. */
  turbo?: boolean;
}

/** Page size sent to the list endpoint (also the server default). A page
 *  shorter than this is the last one. */
export const AI_BATCH_PAGE_SIZE = 20;

/** One page of the list. `before` is the previous page's last `createdAt`. */
export interface AiBatchListQuery {
  filter: AiBatchFilter;
  before?: string;
}

/** The business a submit is for. The name is display-only. */
export interface AiBatchBusinessRef {
  businessId: string;
  businessName: string;
}
